import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  Badge,
} from "@mui/material";
import { Add, Delete, PhotoCamera, DragIndicator, Star } from "@mui/icons-material";
import apiClient from "../utils/apiClient";

const INGREDIENT_UNITS = ["tbsp", "tsp", "gram", "kg", "ml", "liters", "pieces"];

const RecipeForm = ({ open, onClose, recipe: recipeProp, parentRecipeUuid = null, onDelete = null }) => {
  // Use state to track recipe so we can update it after image operations
  const [recipe, setRecipe] = React.useState(recipeProp);
  // Track stable group IDs by ingredient indices (doesn't change when group name changes)
  const groupIdMapRef = useRef(new Map()); // Maps Set of ingredient indices -> stable group ID
  // Track previous recipe UUID to avoid resetting form on image updates
  const prevRecipeUuidRef = useRef(recipeProp?.recipe_uuid);
  
  // Update recipe state when prop changes
  // When creating variant (parentRecipeUuid present), strip images from recipe to avoid showing them
  // Only update if recipe UUID matches (same recipe) or if we don't have a recipe yet
  React.useEffect(() => {
    // Don't overwrite recipe state if we're editing the same recipe and have local changes
    // Only update if it's a different recipe or if we don't have a recipe yet
    setRecipe(prev => {
      if (!prev || !prev.recipe_uuid || !recipeProp || recipeProp.recipe_uuid !== prev.recipe_uuid) {
        // Different recipe or no recipe yet - update with new prop
        if (recipeProp && parentRecipeUuid) {
          // Creating variant - remove images from recipe object
          return { ...recipeProp, images: [] };
        } else if (recipeProp) {
          return recipeProp;
        }
        return prev;
      } else if (recipeProp && recipeProp.recipe_uuid === prev.recipe_uuid) {
        // Same recipe - only update images if they changed, preserve other local state
        if (recipeProp.images && JSON.stringify(recipeProp.images) !== JSON.stringify(prev.images)) {
          return {
            ...prev,
            images: recipeProp.images
          };
        }
        return prev; // No changes needed
      }
      return prev;
    });
  }, [recipeProp, parentRecipeUuid]);
  const [formState, setFormState] = useState({
    name: "",
    description: "",
    serves_amount: 1,
    time: 30,
    parent_recipe_uuid: parentRecipeUuid || null,
    ingredients: [],
    tags: [],
  });

  const [ingredientSearchQueries, setIngredientSearchQueries] = useState({});
  const [searchResults, setSearchResults] = useState({});
  const [error, setError] = useState("");
  const [tagInputValue, setTagInputValue] = useState("");

  useEffect(() => {
    // Only reset form when recipe prop changes, not when internal recipe state changes
    // Check if recipe_uuid changed to avoid resetting on image updates
    const currentRecipeUuid = recipeProp?.recipe_uuid;
    const prevUuid = prevRecipeUuidRef.current;
    
    // Initialize form on first load or when recipe UUID changes
    // Also handle case where recipeProp is initially null but then gets set
    const shouldInitialize = (
      (currentRecipeUuid !== prevUuid) || 
      (prevUuid === null && currentRecipeUuid !== null) ||
      (prevUuid === undefined && recipeProp !== null)
    );
    
    if (shouldInitialize && recipeProp !== null) {
      prevRecipeUuidRef.current = currentRecipeUuid;
      
      if (recipeProp && parentRecipeUuid) {
        // Creating variant - pre-fill with recipe data, name with " - Variant" suffix
        setFormState({
          name: `${recipeProp.name || ""} - Variant`,
          description: recipeProp.description || "",
          serves_amount: recipeProp.serves_amount || 1,
          time: recipeProp.time || 30,
          parent_recipe_uuid: parentRecipeUuid,
          ingredients: (recipeProp.ingredients || []).map(ing => ({
            ingredient_uuid: ing.ingredient?.ingredient_uuid || ing.ingredient_uuid,
            ingredient_name: ing.ingredient?.name,
            unit: ing.unit,
            amount: ing.amount,
            note: ing.note || "",
            group_name: (ing.group_name && ing.group_name.trim() !== "") ? ing.group_name : "",
            order: ing.order || 0,
          })),
          tags: (recipeProp.tags || []).map(tag => tag.name || tag),
        });
      } else if (recipeProp && recipeProp.recipe_uuid) {
        // Editing existing recipe - always initialize with recipe data
        // Use empty string as fallback for name/description if they're null
        setFormState({
          name: recipeProp.name ?? "",
          description: recipeProp.description ?? "",
          serves_amount: recipeProp.serves_amount ?? 1,
          time: recipeProp.time ?? 30,
          parent_recipe_uuid: recipeProp.parent_recipe_uuid ?? null,
          ingredients: (recipeProp.ingredients || []).map(ing => ({
            ingredient_uuid: ing.ingredient?.ingredient_uuid || ing.ingredient_uuid,
            ingredient_name: ing.ingredient?.name,
            unit: ing.unit,
            amount: ing.amount,
            note: ing.note || "",
            group_name: (ing.group_name && ing.group_name.trim() !== "") ? ing.group_name : "",
            order: ing.order || 0,
          })),
          tags: (recipeProp.tags || []).map(tag => tag.name || tag),
        });
      } else if (parentRecipeUuid) {
        // Creating variant without pre-filled data (shouldn't happen, but handle it)
        setFormState(prev => ({
          ...prev,
          parent_recipe_uuid: parentRecipeUuid,
        }));
      }
    }
  }, [recipeProp, parentRecipeUuid]); // Only depend on recipeProp, not internal recipe state
  
  // Also initialize form when dialog opens if we have a recipe but form hasn't been initialized yet
  useEffect(() => {
    if (open && recipeProp && recipeProp.recipe_uuid && !parentRecipeUuid) {
      // Dialog just opened with a recipe - check if form needs initialization
      // Check if form is in its initial empty state (name empty, serves_amount is 1, ingredients empty)
      const isFormEmpty = (
        formState.name === "" && 
        formState.description === "" && 
        formState.serves_amount === 1 && 
        formState.time === 30 &&
        formState.ingredients.length === 0 &&
        formState.tags.length === 0 &&
        recipeProp.name // Recipe actually has a name to populate
      );
      
      if (isFormEmpty && recipeProp.name) {
        // Form is in initial empty state but recipe has data - initialize it
        setFormState({
          name: recipeProp.name ?? "",
          description: recipeProp.description ?? "",
          serves_amount: recipeProp.serves_amount ?? 1,
          time: recipeProp.time ?? 30,
          parent_recipe_uuid: recipeProp.parent_recipe_uuid ?? null,
          ingredients: (recipeProp.ingredients || []).map(ing => ({
            ingredient_uuid: ing.ingredient?.ingredient_uuid || ing.ingredient_uuid,
            ingredient_name: ing.ingredient?.name,
            unit: ing.unit,
            amount: ing.amount,
            note: ing.note || "",
            group_name: (ing.group_name && ing.group_name.trim() !== "") ? ing.group_name : "",
            order: ing.order || 0,
          })),
          tags: (recipeProp.tags || []).map(tag => tag.name || tag),
        });
      }
    }
  }, [open, recipeProp?.recipe_uuid]); // Initialize when dialog opens

  // Search ingredients as user types - per ingredient field
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      Object.entries(ingredientSearchQueries).forEach(async ([index, query]) => {
        if (query && query.trim().length > 0) {
          try {
            const response = await apiClient.get(`/ingredients?search=${encodeURIComponent(query)}`);
            setSearchResults(prev => ({ ...prev, [index]: response.data }));
          } catch (err) {
            console.error(`Failed to search ingredients for field ${index}:`, err);
            setSearchResults(prev => ({ ...prev, [index]: [] }));
          }
        } else {
          setSearchResults(prev => ({ ...prev, [index]: [] }));
        }
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [ingredientSearchQueries]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: name === "serves_amount" || name === "time" ? parseInt(value) || 0 : value,
    }));
  };

  const handleIngredientChange = (index, field, value) => {
    setFormState((prev) => {
      const newIngredients = [...prev.ingredients];
      if (field === "ingredient") {
        // If it's an object with ingredient_uuid, use that (user selected from dropdown)
        if (value && typeof value === 'object' && value.ingredient_uuid) {
          newIngredients[index] = {
            ...newIngredients[index],
            ingredient_uuid: value.ingredient_uuid,
            ingredient_name: value.name,
          };
          // Clear search query when an option is selected
          setIngredientSearchQueries(prev => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });
        } else if (value && typeof value === 'string') {
          // User is typing - update search query for this field
          setIngredientSearchQueries(prev => ({ ...prev, [index]: value }));
          newIngredients[index] = {
            ...newIngredients[index],
            ingredient_uuid: null, // Clear UUID when typing
            ingredient_name: value,
          };
        }
      } else {
        newIngredients[index] = {
          ...newIngredients[index],
          [field]: field === "amount" ? parseFloat(value) || 0 : value,
        };
      }
      return { ...prev, ingredients: newIngredients };
    });
  };

  const handleAddIngredientGroup = () => {
    setFormState((prev) => {
      // Get the max order from all ingredients to place the new group after existing ones
      const maxOrder = prev.ingredients.length > 0 
        ? Math.max(...prev.ingredients.map(ing => ing.order || 0))
        : -1;
      
      // Add a new ingredient as the first in the new group
      const newGroupName = `Group ${Object.keys(
        prev.ingredients.reduce((groups, ing) => {
          const group = ing.group_name || "";
          if (group) groups[group] = true;
          return groups;
        }, {})
      ).length + 1}`;
      
      return {
        ...prev,
        ingredients: [...prev.ingredients, { 
          ingredient_name: "", 
          unit: "gram", 
          amount: 0, 
          note: "", 
          group_name: newGroupName,
          order: maxOrder + 1
        }],
      };
    });
  };

  const handleAddIngredientToGroup = (groupName) => {
    setFormState((prev) => {
      // Find the max order within this group
      const groupIngredients = prev.ingredients.filter(ing => (ing.group_name || "") === (groupName || ""));
      const maxOrderInGroup = groupIngredients.length > 0
        ? Math.max(...groupIngredients.map(ing => ing.order || 0))
        : -1;
      
      // Get the max order overall to ensure new ingredient is after existing groups
      const maxOrderOverall = prev.ingredients.length > 0
        ? Math.max(...prev.ingredients.map(ing => ing.order || 0))
        : -1;
      
      return {
        ...prev,
        ingredients: [...prev.ingredients, { 
          ingredient_name: "", 
          unit: "gram", 
          amount: 0, 
          note: "", 
          group_name: groupName,
          order: maxOrderOverall + 1 // Will be reordered on submit
        }],
      };
    });
  };

  const handleRemoveIngredientGroup = (groupName) => {
    setFormState((prev) => ({
      ...prev,
      ingredients: prev.ingredients.filter(ing => (ing.group_name || "") !== (groupName || "")),
    }));
  };

  const handleGroupNameChange = (oldGroupName, newGroupName) => {
    setFormState((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map(ing => 
        (ing.group_name || "") === (oldGroupName || "") 
          ? { ...ing, group_name: newGroupName }
          : ing
      ),
    }));
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index.toString());
    // Set opacity on the dragged element
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnd = (e) => {
    // Reset opacity
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '1';
    }
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData('text/html'), 10);
    
    if (isNaN(sourceIndex) || sourceIndex === targetIndex) return;

    setFormState((prev) => {
      const sourceIngredient = prev.ingredients[sourceIndex];
      const targetIngredient = prev.ingredients[targetIndex];
      
      if (!sourceIngredient || !targetIngredient) return prev;

      // Only allow reordering within the same group
      const sourceGroup = sourceIngredient.group_name || "";
      const targetGroup = targetIngredient.group_name || "";
      
      if (sourceGroup !== targetGroup) return prev;

      // Get all ingredients in the same group, sorted by order
      const groupIngredients = prev.ingredients
        .map((ing, idx) => ({ ing, idx }))
        .filter(({ ing }) => (ing.group_name || "") === sourceGroup)
        .sort((a, b) => (a.ing.order || 0) - (b.ing.order || 0));

      const sourcePos = groupIngredients.findIndex(({ idx }) => idx === sourceIndex);
      const targetPos = groupIngredients.findIndex(({ idx }) => idx === targetIndex);

      if (sourcePos < 0 || targetPos < 0) return prev;

      // Reorder the group ingredients array
      const reordered = [...groupIngredients];
      const [removed] = reordered.splice(sourcePos, 1);
      reordered.splice(targetPos, 0, removed);

      // Assign new order values sequentially within the group
      const newIngredients = [...prev.ingredients];
      reordered.forEach(({ idx }, newPos) => {
        newIngredients[idx] = { ...newIngredients[idx], order: newPos };
      });

      return { ...prev, ingredients: newIngredients };
    });
  };

  const handleRemoveIngredient = (index) => {
    setFormState((prev) => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index),
    }));
  };


  const handleDelete = async () => {
    if (!recipe || !window.confirm("Are you sure you want to delete this recipe? This action cannot be undone.")) {
      return;
    }

    try {
      await apiClient.delete(`/recipes/${recipe.recipe_uuid}`);
      if (onDelete) {
        onDelete(recipe.recipe_uuid);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete recipe");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      // Sort ingredients: first by group_name (nulls first), then by order
      const sortedIngredients = [...formState.ingredients].sort((a, b) => {
        const groupA = a.group_name || "";
        const groupB = b.group_name || "";
        if (groupA !== groupB) {
          // Empty string (null group) comes first
          if (groupA === "") return -1;
          if (groupB === "") return 1;
          return groupA.localeCompare(groupB);
        }
        return (a.order || 0) - (b.order || 0);
      });

      // Prepare ingredients - use ingredient_name if ingredient_uuid is not set
      // Filter out ingredients without valid ingredient_uuid or ingredient_name first
      const validIngredients = sortedIngredients.filter(ing => {
        // Must have either ingredient_uuid OR non-empty ingredient_name
        return ing.ingredient_uuid || (ing.ingredient_name && ing.ingredient_name.trim());
      });
      
      // Reassign order values sequentially within each group
      const ingredientsToSend = [];
      let currentGroup = null;
      let orderWithinGroup = 0;
      
      validIngredients.forEach(ing => {
        // Convert empty string to null for group_name (to match database schema)
        const ingGroup = (ing.group_name && ing.group_name.trim() !== "") ? ing.group_name.trim() : null;
        // If group changed, reset order counter
        if (ingGroup !== currentGroup) {
          currentGroup = ingGroup;
          orderWithinGroup = 0;
        }
        
        // Build ingredient payload
        const ingredientPayload = {
          unit: ing.unit,
          amount: ing.amount,
          note: ing.note || null,
          group_name: ingGroup,
          order: orderWithinGroup++,
        };
        
        // Add either ingredient_uuid or ingredient_name (but not both)
        if (ing.ingredient_uuid) {
          ingredientPayload.ingredient_uuid = ing.ingredient_uuid;
        } else {
          // ingredient_name is guaranteed to exist and be non-empty due to filter above
          ingredientPayload.ingredient_name = ing.ingredient_name.trim();
        }
        
        ingredientsToSend.push(ingredientPayload);
      });

      const payload = {
        name: formState.name,
        description: formState.description || null,
        serves_amount: formState.serves_amount,
        time: formState.time,
        parent_recipe_uuid: formState.parent_recipe_uuid || null,
        ingredients: ingredientsToSend,
        tags: formState.tags.filter(tag => tag && tag.trim() !== "").map(tag => tag.trim().toLowerCase()),
      };

      let response;
      if (recipe && parentRecipeUuid) {
        // Cloning a recipe - use clone endpoint which copies ingredients and steps
        // Include ingredients and tags in payload so user can modify them before cloning
        const clonePayload = {
          name: payload.name,
          description: payload.description || null,
          serves_amount: payload.serves_amount,
          time: payload.time,
          ingredients: payload.ingredients,  // Include ingredients from form
          tags: payload.tags,  // Include tags from form
          // Steps are managed separately via individual endpoints, so don't include them
        };
        response = await apiClient.post(`/recipes/${recipe.recipe_uuid}/clone`, clonePayload);
        onClose(true, response.data);
      } else if (recipe) {
        // Update existing recipe - don't include steps, they're managed separately
        response = await apiClient.put(`/recipes/${recipe.recipe_uuid}`, payload);
        onClose(true, response.data);
      } else {
        // Create new recipe - include empty steps array
        payload.steps = []; // Steps are added separately after recipe creation
        response = await apiClient.post("/recipes", payload);
        // For new recipes, fetch the full recipe data including any defaults
        const newRecipeResponse = await apiClient.get(`/recipes/${response.data.recipe_uuid}`);
        onClose(true, newRecipeResponse.data);
      }
    } catch (err) {
      console.error("Failed to save recipe:", err);
      setError(err.response?.data?.detail || "Failed to save recipe. Please try again.");
    }
  };

  const handleDialogClose = () => {
    setFormState({
      name: "",
      description: "",
      serves_amount: 1,
      time: 30,
      parent_recipe_uuid: parentRecipeUuid || null,
      ingredients: [],
      tags: [],
    });
    setError("");
    setIngredientSearchQueries({});
    setSearchResults({});
    setTagInputValue("");
    onClose(false);
  };

  const handleImageUpload = async (file) => {
    if (!recipe) return; // Can only upload images when editing existing recipe
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await apiClient.post(`/recipes/${recipe.recipe_uuid}/images`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      // Update only the images array in recipe state to avoid resetting the form
      setRecipe(prev => ({
        ...prev,
        images: [...(prev.images || []), response.data]
      }));
    } catch (err) {
      console.error("Failed to upload image:", err);
      setError(err.response?.data?.detail || "Failed to upload image");
    }
  };

  const handleImageDelete = async (imageUuid) => {
    if (!recipe || !window.confirm("Are you sure you want to delete this image?")) {
      return;
    }
    
    try {
      await apiClient.delete(`/recipes/${recipe.recipe_uuid}/images/${imageUuid}`);
      // Update only the images array in recipe state to avoid resetting the form
      setRecipe(prev => ({
        ...prev,
        images: (prev.images || []).filter(img => img.image_uuid !== imageUuid)
      }));
    } catch (err) {
      console.error("Failed to delete image:", err);
      setError(err.response?.data?.detail || "Failed to delete image");
    }
  };

  const handleSetHeaderImage = async (imageUuid, isHeader) => {
    if (!recipe) return;
    
    try {
      await apiClient.put(`/recipes/${recipe.recipe_uuid}/images/${imageUuid}/header?is_header=${isHeader}`);
      // Update only the images array in recipe state to avoid resetting the form
      setRecipe(prev => ({
        ...prev,
        images: (prev.images || []).map(img => 
          img.image_uuid === imageUuid 
            ? { ...img, is_header_image: isHeader }
            : isHeader && img.is_header_image
            ? { ...img, is_header_image: false } // Unset previous header
            : img
        )
      }));
    } catch (err) {
      console.error("Failed to set header image:", err);
      setError(err.response?.data?.detail || "Failed to set header image");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {recipe && recipe.recipe_uuid && !parentRecipeUuid ? "Edit Recipe" : parentRecipeUuid ? "Create variant recipe" : "Create Recipe"}
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Recipe Name"
            name="name"
            value={formState.name}
            onChange={handleChange}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formState.description}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={3}
          />

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Serves"
                name="serves_amount"
                type="number"
                value={formState.serves_amount}
                onChange={handleChange}
                margin="normal"
                required
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Time (minutes)"
                name="time"
                type="number"
                value={formState.time}
                onChange={handleChange}
                margin="normal"
                required
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>

          {/* Tags Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Tags</Typography>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={formState.tags}
              inputValue={tagInputValue}
              onInputChange={(event, newInputValue) => {
                setTagInputValue(newInputValue);
              }}
              onChange={(event, newValue, reason) => {
                if (reason === 'createOption' || reason === 'selectOption') {
                  // When user presses Enter or selects, add the tag
                  const newTag = typeof newValue[newValue.length - 1] === 'string' 
                    ? newValue[newValue.length - 1].trim().toLowerCase()
                    : newValue[newValue.length - 1];
                  
                  if (newTag && !formState.tags.includes(newTag)) {
                    setFormState(prev => ({
                      ...prev,
                      tags: newValue.map(tag => typeof tag === 'string' ? tag.trim().toLowerCase() : tag),
                    }));
                    setTagInputValue("");
                  } else {
                    setFormState(prev => ({
                      ...prev,
                      tags: newValue,
                    }));
                  }
                } else {
                  setFormState(prev => ({
                    ...prev,
                    tags: newValue,
                  }));
                }
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={index}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add tags"
                  placeholder="Type and press Enter to add a tag"
                  helperText="Tags are automatically converted to lowercase"
                />
              )}
            />
          </Box>

          {/* Ingredients Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Ingredients</Typography>

            {(() => {
              // Sort and group ingredients for display
              const sortedIngredients = [...formState.ingredients].sort((a, b) => {
                const groupA = a.group_name || "";
                const groupB = b.group_name || "";
                if (groupA !== groupB) {
                  if (groupA === "") return -1;
                  if (groupB === "") return 1;
                  return groupA.localeCompare(groupB);
                }
                return (a.order || 0) - (b.order || 0);
              });

              // Group ingredients by group_name, using stable IDs based on ingredient indices
              const groupedIngredients = {};
              const groupIndicesMap = {}; // Maps groupName -> Set of ingredient indices
              
              sortedIngredients.forEach((ing) => {
                const groupName = ing.group_name || "";
                const originalIndexInState = formState.ingredients.findIndex(orig => orig === ing);
                
                if (!groupedIngredients[groupName]) {
                  groupedIngredients[groupName] = [];
                  groupIndicesMap[groupName] = new Set();
                }
                
                groupedIngredients[groupName].push({ ...ing, _originalIndex: originalIndexInState });
                groupIndicesMap[groupName].add(originalIndexInState);
              });

              // Get or create stable IDs for each group based on ingredient indices
              const groupStableIds = {};
              Object.keys(groupIndicesMap).forEach(groupName => {
                const indicesKey = Array.from(groupIndicesMap[groupName]).sort().join(',');
                
                if (!groupIdMapRef.current.has(indicesKey)) {
                  // Create new stable ID (use min index as the ID)
                  const minIndex = Math.min(...groupIndicesMap[groupName]);
                  groupIdMapRef.current.set(indicesKey, minIndex);
                }
                
                groupStableIds[groupName] = groupIdMapRef.current.get(indicesKey);
              });

              // Convert to sorted array
              const groupsArray = Object.keys(groupedIngredients)
                .sort((a, b) => {
                  if (a === "" && b !== "") return -1;
                  if (a !== "" && b === "") return 1;
                  return a.localeCompare(b);
                })
                .map(groupName => ({
                  groupName,
                  stableId: groupStableIds[groupName],
                  ingredients: groupedIngredients[groupName]
                }));

              // If no groups exist, show only the "Add Group" button
              if (groupsArray.length === 0) {
                return (
                  <Button 
                    startIcon={<Add />} 
                    onClick={handleAddIngredientGroup} 
                    size="small"
                    variant="outlined"
                  >
                    Add Ingredient Group
                  </Button>
                );
              }

              return (
                <>
                  {groupsArray.map(({ stableId, groupName, ingredients: groupIngredients }) => {
                    return (
                      <Card key={`group-stable-${stableId}`} sx={{ mb: 3 }}>
                        <CardContent>
                          {/* Group Header */}
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <TextField
                              value={groupName || ""}
                              onChange={(e) => handleGroupNameChange(groupName, e.target.value)}
                              placeholder="Group name (e.g., For the cake)"
                              size="small"
                              sx={{ flexGrow: 1, mr: 2 }}
                            />
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleRemoveIngredientGroup(groupName)}
                              title="Remove group"
                            >
                              <Delete />
                            </IconButton>
                          </Box>

                          {/* Ingredients in this group */}
                          {groupIngredients.map((ingredient, groupIndex) => {
                            const index = ingredient._originalIndex;
                            return (
                              <Box 
                                key={index} 
                                sx={{ 
                                  mb: 2, 
                                  pl: 2, 
                                  borderLeft: "2px solid", 
                                  borderColor: "divider",
                                  cursor: 'grab',
                                  '&:active': {
                                    cursor: 'grabbing'
                                  }
                                }}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={handleDragOver}
                                onDragEnd={handleDragEnd}
                                onDrop={(e) => handleDrop(e, index)}
                              >
                                <Grid container spacing={2} alignItems="flex-start">
                                  <Grid item xs={1}>
                                    <Box
                                      sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        height: '100%',
                                        color: 'text.secondary',
                                        mt: 1,
                                        pointerEvents: 'none' // Let drag work on parent Box
                                      }}
                                    >
                                      <DragIndicator fontSize="small" />
                                    </Box>
                                  </Grid>
                                  <Grid item xs={3}>
                                    <Autocomplete
                                      freeSolo
                                      options={searchResults[index] || []}
                                      getOptionLabel={(option) =>
                                        typeof option === "string" ? option : option.name
                                      }
                                      value={
                                        ingredient.ingredient_uuid && searchResults[index]
                                          ? searchResults[index].find(opt => opt.ingredient_uuid === ingredient.ingredient_uuid) || ingredient.ingredient_name || ""
                                          : ingredient.ingredient_name || ""
                                      }
                                      inputValue={ingredientSearchQueries[index] !== undefined 
                                        ? ingredientSearchQueries[index] 
                                        : ingredient.ingredient_name || ""
                                      }
                                      onInputChange={(e, newValue, reason) => {
                                        // Update search query when user types
                                        if (reason === 'input') {
                                          setIngredientSearchQueries(prev => ({ ...prev, [index]: newValue }));
                                        } else if (reason === 'reset') {
                                          setIngredientSearchQueries(prev => {
                                            const updated = { ...prev };
                                            delete updated[index];
                                            return updated;
                                          });
                                        }
                                      }}
                        onChange={(e, newValue, reason) => {
                          // When user selects an option from dropdown, use the full object
                          if (reason === 'selectOption' && newValue && typeof newValue === 'object' && newValue.ingredient_uuid) {
                            handleIngredientChange(index, "ingredient", newValue);
                          } else if (reason === 'createOption' || (reason === 'selectOption' && typeof newValue === 'string')) {
                            // User typed a new ingredient name or selected from free text
                            handleIngredientChange(index, "ingredient", newValue);
                          } else if (reason === 'clear') {
                            handleIngredientChange(index, "ingredient", "");
                            setIngredientSearchQueries(prev => {
                              const updated = { ...prev };
                              delete updated[index];
                              return updated;
                            });
                          }
                        }}
                        onBlur={(e) => {
                          // When user leaves the field, save the current input value if it's typed text
                          // Use the search query if available (current typed text), otherwise use existing ingredient_name
                          const currentInputValue = ingredientSearchQueries[index];
                          if (currentInputValue !== undefined) {
                            // User has typed something - save it as ingredient_name if no UUID is set
                            if (currentInputValue.trim() && !ingredient.ingredient_uuid) {
                              handleIngredientChange(index, "ingredient", currentInputValue.trim());
                            }
                            // Clear the search query since we've saved the value
                            setIngredientSearchQueries(prev => {
                              const updated = { ...prev };
                              delete updated[index];
                              return updated;
                            });
                          }
                        }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Ingredient"
                                          placeholder="Search existing or type new"
                                          helperText={ingredient.ingredient_uuid ? "✓ Using existing ingredient" : ingredient.ingredient_name ? "Will create new ingredient" : ""}
                                        />
                                      )}
                                      filterOptions={(options, params) => {
                                        // Don't filter client-side, backend already filtered
                                        return options;
                                      }}
                                      isOptionEqualToValue={(option, value) => {
                                        if (typeof option === 'string' || typeof value === 'string') {
                                          return option === value;
                                        }
                                        return option.ingredient_uuid === value.ingredient_uuid;
                                      }}
                                      componentsProps={{
                                        popper: {
                                          style: { zIndex: 1300 }
                                        }
                                      }}
                                    />
                                  </Grid>
                                <Grid item xs={2}>
                                  <TextField
                                    fullWidth
                                    label="Amount"
                                    type="number"
                                    value={ingredient.amount}
                                    onChange={(e) =>
                                      handleIngredientChange(index, "amount", e.target.value)
                                    }
                                    inputProps={{ step: "0.1", min: 0 }}
                                  />
                                </Grid>
                                <Grid item xs={2}>
                                  <FormControl fullWidth>
                                    <InputLabel>Unit</InputLabel>
                                    <Select
                                      value={ingredient.unit || "gram"}
                                      onChange={(e) =>
                                        handleIngredientChange(index, "unit", e.target.value)
                                      }
                                      label="Unit"
                                    >
                                      {INGREDIENT_UNITS.map((unit) => (
                                        <MenuItem key={unit} value={unit}>
                                          {unit}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={3}>
                                  <TextField
                                    fullWidth
                                    label="Note (optional)"
                                    value={ingredient.note || ""}
                                    onChange={(e) =>
                                      handleIngredientChange(index, "note", e.target.value)
                                    }
                                    placeholder="e.g., chopped, diced, etc."
                                  />
                                </Grid>
                                <Grid item xs={1}>
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => handleRemoveIngredient(index)}
                                    title="Remove ingredient"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Box>
                            );
                          })}

                          {/* Add Ingredient button for this group */}
                          <Box sx={{ pl: 2 }}>
                            <Button 
                              startIcon={<Add />} 
                              onClick={() => handleAddIngredientToGroup(groupName)} 
                              size="small"
                              variant="outlined"
                            >
                              Add Ingredient
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}

                  {/* Add Group button */}
                  <Button 
                    startIcon={<Add />} 
                    onClick={handleAddIngredientGroup} 
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1 }}
                  >
                    Add Ingredient Group
                  </Button>
                </>
              );
            })()}
          </Box>

          {/* Gallery Section (only when editing existing recipe, not when creating variant) */}
          {recipe && recipe.recipe_uuid && !parentRecipeUuid && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Gallery
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                Add images to your recipe. Click the star icon to set an image as the header image. All images can be inserted into steps.
              </Typography>
              
              {/* Gallery Grid */}
              {recipe.images && recipe.images.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {recipe.images.map((image) => (
                    <Grid item xs={6} sm={4} md={3} key={image.image_uuid}>
                      <Box 
                        sx={{ 
                          position: "relative",
                          "&:hover .header-star-button": {
                            opacity: 1,
                            visibility: "visible",
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={`${apiClient.defaults.baseURL || ""}/static/recipe_images/${image.image_uuid}_thumb.jpg`}
                          alt="Recipe"
                          sx={{
                            width: "100%",
                            height: "auto",
                            borderRadius: 1,
                            aspectRatio: "16/10",
                            objectFit: "cover",
                            "&:hover": {
                              opacity: 0.8,
                            },
                          }}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={async (e) => {
                            e.stopPropagation();
                            await handleImageDelete(image.image_uuid);
                          }}
                          sx={{
                            position: "absolute",
                            top: 4,
                            left: 4,
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 1)",
                            },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                        <IconButton
                          className="header-star-button"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetHeaderImage(image.image_uuid, !image.is_header_image);
                          }}
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            opacity: image.is_header_image ? 1 : 0,
                            visibility: image.is_header_image ? "visible" : "hidden",
                            transition: "opacity 0.2s, visibility 0.2s",
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 1)",
                            },
                          }}
                          title={image.is_header_image ? "Click to unset as header" : "Click to set as header"}
                        >
                          <Star 
                            fontSize="small" 
                            sx={{ 
                              color: "primary.main",
                              "&:hover": {
                                color: "text.primary",
                              },
                            }} 
                          />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
              
              {/* Upload button */}
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCamera />}
                sx={{
                  borderStyle: "dashed",
                }}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      handleImageUpload(e.target.files[0]);
                    }
                  }}
                />
              </Button>
            </Box>
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1 }}>
          {recipe && recipe.recipe_uuid && !parentRecipeUuid && (
            <Button onClick={handleDelete} variant="contained" color="error" sx={{ mr: 1 }}>
              Delete Recipe
            </Button>
          )}
        </Box>
        <Button onClick={handleDialogClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {recipe && recipe.recipe_uuid && !parentRecipeUuid ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipeForm;


